import { IAssetCategoryTypeResponse } from '@kcftech/prometheus-client';
import { useMutation } from '@tanstack/react-query';
import {
    SwitchInlineEditor,
    SwitchInlineEditorProps,
} from 'components/inline_edit/ToggleInlineEditor';
import { useNotificationContext } from 'contexts/NotificationContext';
import { usePermissions } from 'contexts/PermissionsContext';
import { useState } from 'react';
import { updateAssetType } from 'utils/asset_classification';

export type AssetTypeStatusInlineEditorProps = Omit<
    SwitchInlineEditorProps,
    'value' | 'onConfirm' | 'validate'
> &
    Readonly<{
        assetType: IAssetCategoryTypeResponse;
        onUpdate: () => void;
    }>;

export function AssetTypeStatusInlineEditor({
    assetType,
    onUpdate,
}: AssetTypeStatusInlineEditorProps) {
    const { canEditAssetType } = usePermissions();
    const { showToast } = useNotificationContext();
    const [isActive, setIsActive] = useState(assetType.Active);

    const { mutate: changeAssetTypeStatus, isPending } = useMutation({
        meta: { name: 'update-asset-type' },
        mutationFn: async (updatedStatus: boolean) =>
            updateAssetType(assetType.Id, {
                Active: updatedStatus,
            }),

        onSuccess: (newAssetType) => {
            setIsActive(newAssetType.Active);
            onUpdate();
        },

        onError: () => {
            showToast({
                type: 'error',
                content: 'Failed to change asset type status',
            });
        },
    });

    return (
        <div
            aria-label={`${assetType.Name} status switch`}
            title="Edit asset type status"
        >
            <SwitchInlineEditor
                renderValue={(value) => (value ? 'Active' : 'Inactive')}
                value={isActive}
                isDisabled={!canEditAssetType || isPending}
                onConfirm={(val) => changeAssetTypeStatus(val ?? false)}
            />
        </div>
    );
}
